import { useSearchParams } from 'react-router-dom';
import { atom, useRecoilState } from 'recoil';
import { useCallback } from 'react';
import deleteSearchParamByKeyValue from '../helpers';
import { resetPagination } from '../../../Pagination/shared/hooks/usePagination';

const uncommittedSearchParamsAtom = atom<URLSearchParams>({
    key: 'uncommitted-search-params',
    default: new URLSearchParams()
});

/**
 * A hook for manipulating URLSearchParams before they are committed.
 * The reason we use this hook instead of the useSearchParams hook is because we want to only apply the URLSearchParams when the user clicks apply filters,
 * therefore we cannot modify the UrlSearchParams directly, we need this commit stage first.
 * @param queryStringName
 * @returns
 */
export default function useUncommittedSearchParams() {
    const [uncommittedSearchParams, setUncommittedSearchParams] = useRecoilState(uncommittedSearchParamsAtom);
    const [searchParams, setSearchParams] = useSearchParams();

    /**
     * Has any uncommittedSearchParams
     */
    const hasUncommittedSearchParams =
        uncommittedSearchParams && uncommittedSearchParams.toString() !== searchParams.toString();

    /**
     * Commit the pending SearchParams to the URL
     * @returns
     */
    const commitUncommittedSearchParams = useCallback(() => {
        if (hasUncommittedSearchParams) {
            setSearchParams(resetPagination(uncommittedSearchParams));
        }
    }, [hasUncommittedSearchParams, setSearchParams, uncommittedSearchParams]);
    /**
     * Resets the uncommitted Search params state to the current URL search params state.
     */
    const resetUncommittedSearchParams = useCallback(
        () => setUncommittedSearchParams(new URLSearchParams(searchParams)),
        [searchParams, setUncommittedSearchParams]
    );

    /**
     * setUncommittedSearchParams (but makes sure the state is updated by creating a new UrlSearchParams object)
     * @param valOrUpdater
     */
    const handleSetUncommittedSearchParams = useCallback(
        (valOrUpdater: (URLSearchParams: URLSearchParams) => URLSearchParams) => {
            if (valOrUpdater instanceof Function) {
                setUncommittedSearchParams((prev) => {
                    const newSearchParams = valOrUpdater(prev);

                    // We have to create a new UrlSearchParams obj otherwise the state won't update.
                    return new URLSearchParams(newSearchParams);
                });
            } else {
                setUncommittedSearchParams(new URLSearchParams(valOrUpdater));
            }
        },
        [setUncommittedSearchParams]
    );

    /**
     * Check if a queryString exists in the uncommitted UrlSearchParams object
     * @param queryStringName
     * @returns
     */
    const isUncommittedSearchParamActive = (queryStringName: string) => uncommittedSearchParams.has(queryStringName);

    /**
     * Check if a value is  exists in the uncommitted UrlSearchParams object
     * @param value
     * @returns boolean
     */
    const isUncommittedSearchParamValueActive = useCallback(
        (queryStringName: string, value: string) => uncommittedSearchParams.getAll(queryStringName).includes(value),
        [uncommittedSearchParams]
    );

    /**
     * Add a uncommitted SearchParam by querystring and value
     */
    const addUncommittedSearchParamByValue = useCallback(
        (queryStringName: string, value: string) => {
            const isSearchParamValueActive = isUncommittedSearchParamValueActive(queryStringName, value);

            if (isSearchParamValueActive) return;

            handleSetUncommittedSearchParams((prevUncommittedSearchParams) => {
                prevUncommittedSearchParams.append(queryStringName, value);
                return prevUncommittedSearchParams;
            });
        },
        [handleSetUncommittedSearchParams, isUncommittedSearchParamValueActive]
    );

    /**
     * Remove a uncommitted SearchParam by query string and value
     */
    const removeUncommittedSearchParam = useCallback(
        (queryStringName: string) => {
            handleSetUncommittedSearchParams((prevUncommittedSearchParams) => {
                prevUncommittedSearchParams.delete(queryStringName);
                return prevUncommittedSearchParams;
            });
        },
        [handleSetUncommittedSearchParams]
    );

    /**
     * Remove a uncommitted SearchParam by query string and value
     */
    const removeUncommittedSearchParamByValue = useCallback(
        (queryStringName: string, value: string) => {
            handleSetUncommittedSearchParams((prevUncommittedSearchParams) => {
                deleteSearchParamByKeyValue(prevUncommittedSearchParams, queryStringName, value);
                return prevUncommittedSearchParams;
            });
        },
        [handleSetUncommittedSearchParams]
    );

    /**
     * update a uncommitted SearchParam by querystring (overwrites all other instances of that query string)
     */
    const updateUncommittedSearchParam = useCallback(
        (queryStringName: string, value: string) => {
            handleSetUncommittedSearchParams((previousSearchParams) => {
                previousSearchParams.delete(queryStringName);
                previousSearchParams.append(queryStringName, value);

                return previousSearchParams;
            });
        },
        [handleSetUncommittedSearchParams]
    );

    /**
     * Toggle a queryString value on/off
     * @param value
     * @returns void
     */
    const handleToggleUncommittedSearchParamByValue = useCallback(
        (queryStringName: string, value: string) => {
            const isSearchParamValueActive = isUncommittedSearchParamValueActive(queryStringName, value);

            if (!isSearchParamValueActive) {
                addUncommittedSearchParamByValue(queryStringName, value);
            } else {
                removeUncommittedSearchParamByValue(queryStringName, value);
            }
        },
        [addUncommittedSearchParamByValue, isUncommittedSearchParamValueActive, removeUncommittedSearchParamByValue]
    );

    return {
        commitUncommittedSearchParams,
        addUncommittedSearchParamByValue,
        removeUncommittedSearchParam,
        removeUncommittedSearchParamByValue,
        updateUncommittedSearchParam,
        toggleUncommittedSearchParam: handleToggleUncommittedSearchParamByValue,
        isUncommittedSearchParamActive,
        isUncommittedSearchParamValueActive,
        uncommittedSearchParams,
        resetUncommittedSearchParams,
        hasUncommittedSearchParams
    };
}
